import { AlertTriangle, Check, Dices, Link2, OctagonAlert, RotateCcw, Smartphone } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TIER_MARK, useRecalledHere, VERDICT_GLYPH } from './answer-ref-context';
import { BlockCard, BlockPanelCard } from './block-card';
import { SlashedLabel, chipClass, ChipDivider, ChipEvalIcon, UnreachableChip } from './eval-chip';
import { useBlockIdentity } from './fired-states';

import type { BlockIssue } from './block-issues';
import type { Block } from '@/types/experience';
import type { GradingMode } from '@agavi/blocks';

import { IconButton } from '@/components/ui/icon-button';
import { Switch } from '@/components/ui/switch';


/** The evaluation bucket a chip belongs to, mirroring the universal set of default feedback
 * states every block shares (see use-input-block-feedback.ts's positiveTriggerForMode). Rendered
 * as its own labeled cluster in the legend so a fired catch-all (Incorrect) never reads as
 * indistinguishable from a fired, specifically-authored Targeted chip just because both happen
 * to resolve to the same option/value text. `any_answer`/`any_response` are deliberately their
 * OWN categories, never folded into `correct` — even though both render with the same green
 * 'correct' evaluation color, bucketing a survey/optional "any answer accepted" chip under a
 * category literally labeled "Correct" is the same misleading-for-analysis pattern this whole
 * naming scheme exists to avoid, just one layer up (UI grouping instead of the raw trigger
 * string). */
export type TestBarCategory = 'unanswered' | 'incomplete' | 'correct' | 'any_answer' | 'any_response' | 'targeted' | 'incorrect';

/** One possible feedback state, rendered as a permanent chip in the tester-bar legend regardless
 * of whether it actually fired this test — every state a block can ever produce (No Answer,
 * Correct, every Targeted, Default Incorrect) is always shown, so the legend doubles as a setup
 * overview even before any test runs. `condition` reuses whichever chip/label component the
 * block's own feedback editor already renders for that state rather than inventing new copy —
 * titles are deliberately omitted here (they're hidden everywhere else too — see feedback-card.tsx
 * — since the condition already says everything a teacher needs at a glance). */
/**
 * A connector between two chips of the same category — "or" between alternatives, "and" between
 * terms that must all hold. Shared, because the words are only half the point: the feedback panel
 * has always printed them between its own chips, and a legend that lists three chips with nothing
 * between them does not say whether the student needs one of them or all three. Blocks that fanned
 * out without connectors were silently describing a different condition from the panel next to them.
 */
export function connectorChip(id: string, label: string, category: TestBarChip['category']): TestBarChip {
  return { id, condition: label, evaluation: 'neutral', active: false, category, kind: 'connector' };
}

export interface TestBarChip {
  id: string;
  condition: React.ReactNode;
  evaluation: 'neutral' | 'correct' | 'incorrect';
  /** Whether this state actually fired in the current test — dims when false. Always false for
   * every chip before any test has run (hasResult === false), which is what makes the untested
   * legend read as "dimmed setup overview" rather than "nothing to show yet." */
  active: boolean;
  /** False when this state can structurally never fire — e.g. Default Incorrect once every
   * non-correct option/value has its own targeted feedback, so the catch-all has nothing left
   * to catch. Renders as a distinct disabled/dashed chip, ignoring `evaluation`/`active`
   * entirely (an unreachable state is never "the fired one," no matter what). Defaults to true. */
  reachable?: boolean;
  /** Overrides the "Unreachable" chip's own text. Only for a chip that is ONE TERM of a larger
   * condition rather than a whole state — FITB's Correct is a conjunction across blanks, so the
   * dead term has to say WHICH blank ("Blank 2: Unreachable") or the row reads as a single
   * unreachable state and the teacher has nothing to go and fix. Left unset everywhere else. */
  unreachableLabel?: string;
  /** Draws a line across the whole chip: this condition names a correct answer that was NOT
   * selected. Chip-level rather than a slash the block draws over its own label — crossing only
   * the label negates the value ("not 40") instead of the condition ("40 was not selected"). */
  slashed?: boolean;
  /** This chip is not a TERM in the condition — it names an option that constrains nothing (MCQ's
   *  neutral options, which are optional either way). Drawn back so it reads as trailing the
   *  expression rather than joining it. Set from `ConditionChip.muted`, so the legend and the
   *  feedback panel draw the same distinction from one derivation instead of each inventing it. */
  muted?: boolean;
  category: TestBarCategory;
  /**
   * Names this chip's cluster in place of its category's own label.
   *
   * For a block whose targeted states fall into several NAMED groups: Numerical's legend used to
   * print "TARGETED" twice — once for units, once for everything else — which says nothing about
   * which row is which, while the feedback panel beside it named all four. Chips sharing a
   * `track` share a cluster, so the label belongs to the track; it is set per chip because that is
   * where the block already decides everything else about the row.
   */
  categoryLabel?: string;
  /** Groups chips into their own wrapping row, for blocks with parallel evaluation tracks (e.g.
   * Numerical's value-track vs. unit-track) where one flat sequence would imply a false single
   * ordering. Chips sharing no track (or all omitting it) render together in one row; within a
   * track, chips still sub-group by category. */
  track?: string;
  /** Marks this entry as a plain inline "or"/"and" separator between two real chips in the same
   * category — not a feedback state itself, so it renders as plain muted text with no icon/color/
   * active-dimming, inline in the same wrapping row (e.g. FITB's multi-blank correct/incorrect fan-
   * out, where correctness is AND across blanks but OR across a blank's own alternative words). */
  kind?: 'connector';
}

export interface TestBarProps {
  /** Live (evaluate on every change) vs. submit-gated. */
  continuous: boolean;
  onContinuousChange: (next: boolean) => void;
  /** Runs one evaluation pass — only shown/meaningful while !continuous. */
  onSubmit: () => void;
  /** Whether an evaluation has actually run yet (submitted, or continuous + touched) — passed
   * through for blocks that want it, though the chip legend itself renders the same either way
   * (nothing highlighted pre-test vs. the fired chip(s) highlighted post-test). */
  hasResult: boolean;
  /** Every possible feedback state for this block, in display order — see TestBarChip. */
  chips: TestBarChip[];
  /**
   * Why the chip list might be empty, which the bar cannot infer.
   *
   * The empty state used to say "No feedback options available in Optional mode" whenever there
   * were no chips — but the CONDITION was only "no chips", so a fresh graded block with nothing set
   * up yet was told it was in a mode it was not in. Optional genuinely has no feedback to show;
   * every other mode has plenty and simply has no answer to hang it on.
   *
   * Optional so the two frames can INJECT it — both already own the mode — rather than eleven
   * blocks each threading it through a second time. Absent, the neutral message is used, which is
   * the safe direction: it is true whenever the mode is not Optional and merely unspecific when it
   * is, where the old default was actively false for every graded block.
   */
  gradingMode?: GradingMode;
  /** Clears the block back to its untouched/no-answer state — the only way back to that state
   * once continuous mode (or a submit) has evaluated a real answer. */
  onReset: () => void;
  /** Omit entirely for blocks with no rollable variables to test against. */
  onReroll?: () => void;
  /** Teacher-only diagnostics for the current test run — e.g. setup warnings/errors. Belongs here
   * (not the card's own content area, `children`) because that area is meant to eventually freeze
   * into the exact canonical display students see; anything a student should never see belongs
   * in this panel instead. Rendered below the chip legend, separated by its own divider, only
   * while there's something to show. */
  notices?: React.ReactNode;
  /** Same idea as `notices`, but for a result a teacher wants to see immediately, above the chip
   * legend, right under the continuous/submit/reroll controls — e.g. which branch resolved for
   * this test, with a jump-to-edit action. Kept as a separate slot from `notices` (rather than
   * just prepending it there) since the two serve different reading orders: this is "here's what
   * just happened," read first; `notices` is "here's what's still wrong," read after the legend. */
  topNotices?: React.ReactNode;
  /** Stacks each category's label above its chips instead of beside them — for the phone
   * landscape Test rail (~135px usable), where the default label-beside-chips grid leaves too
   * little width for even a single chip to render on one line. Confirmed live: without this, a
   * chip like "No Answer" doesn't wrap, it gets visually clipped by the bar's own
   * `overflow-hidden` (needed elsewhere for rounded corners) — label-above-chips gives the chip
   * row the column's full width instead of the ~80px 1fr remainder. Omit for the existing
   * default/wider usage (desktop cards, portrait phone bar) — unchanged side-by-side layout. */
  compact?: boolean;
  /** Lets this bar be squeezed to whatever bounded height its wrapper gives it (the phone
   * dialog's capped bar / rail contexts — see input-block-frame.tsx), scrolling the chip legend
   * and notices internally instead of growing past it. Opt-in, not automatic: desktop's card is
   * itself one of several `flex-1` siblings in the block list's own scrolling column, so once
   * this bar is allowed to shrink (`min-h-0`), that ancestor chain happily squeezes the whole
   * card down to an equal share and scrolls the legend internally too — confirmed live, and not
   * the desired behavior there (today, a tall legend should grow the card and let the page
   * scroll, exactly as before). Omit for the two existing desktop call sites; pass only from the
   * phone dialog's Test-mode renders. */
  constrainedHeight?: boolean;
  /** Renders only part of the bar — for the phone landscape Test rail, which splits the bar
   * across two locations instead of stacking everything in one place: 'controls' (continuous
   * switch, submit, reroll/reset, topNotices) goes in a full-width bar below both columns, since
   * all of that wants width a ~144px rail can't give it (confirmed live: the submit button was
   * pushed off-screen, and topNotices' equation text didn't fit); 'legend' (the chip legend +
   * notices) goes in the right rail instead, alongside the Edit/Test toggle, since a categorized
   * chip list is the one thing that already reads fine narrow. Omit (default 'all') for the
   * existing single-piece usage (desktop cards, portrait phone bar) — unchanged. */
  renderSection?: 'all' | 'controls' | 'legend';
}

// The test bar's chips are the same chip as the feedback panel's; these were a third copy of the
// icon and tone tables. Kept as local names only so the call sites below read as they did.
function testBarEvalIcon(evaluation: TestBarChip['evaluation']) {
  return <ChipEvalIcon evaluation={evaluation} />;
}

/**
 * Specific before general, in every mode — the rule the feedback panel already follows.
 *
 * The catch-all used to sit ABOVE `targeted`, which was invisible while survey and optional could
 * not fire a targeted state and simply wrong once they could: the bar announced what catches
 * everything else before naming the answers the teacher had singled out. Graded is unaffected,
 * because `any_answer`/`any_response` never appear there — `correct` stays first as the terminal
 * positive, and `incorrect` stays last as graded's own catch-all.
 */
const CATEGORY_ORDER: TestBarCategory[] = ['unanswered', 'incomplete', 'correct', 'targeted', 'any_answer', 'any_response', 'incorrect'];

/** Groups chips into their authored track order — a Map (not a plain object) so a track key that
 * happens to look numeric still preserves insertion order rather than being reordered. Within
 * each track, chips are further split into their (fixed-order) category clusters. */
export function groupChipsByTrack(chips: TestBarChip[]): Array<{ track: string | undefined; anyTargeted: boolean; categories: Array<{ category: TestBarCategory; chips: TestBarChip[] }> }> {
  const order: (string | undefined)[] = [];
  const byTrack = new Map<string | undefined, TestBarChip[]>();
  for (const c of chips) {
    if (!byTrack.has(c.track)) { byTrack.set(c.track, []); order.push(c.track); }
    byTrack.get(c.track)!.push(c);
  }
  // Across every track, not within one: numerical groups its targeted chips onto a VALUE track
  // while the catch-all carries none, so a per-track answer would always be "nothing targeted".
  const anyTargeted = chips.some(c => c.category === 'targeted');
  return order.map(track => {
    const trackChips = byTrack.get(track)!;
    const categories = CATEGORY_ORDER
      .map(category => ({ category, chips: trackChips.filter(c => c.category === category) }))
      .filter(g => g.chips.length > 0);
    return { track, categories, anyTargeted };
  });
}

/** Every category keeps its label — even 'correct', whose Check+green combo is otherwise
 * unambiguous, so that every row in the legend reads uniformly rather than one row looking
 * like an omission. */
export function testBarCategoryLabel(category: TestBarCategory, t: (key: string) => string, hasTargeted = false): string {
  return category === 'unanswered' ? t('blocks.testBar.categoryUnanswered')
    : category === 'incomplete' ? t('blocks.testBar.categoryIncomplete')
    : category === 'correct' ? t('blocks.testBar.categoryCorrect')
    /**
     * A row LABEL names the group; the chip inside it states the condition. Spelling "Any" in
     * both made the row read as its own contents — "Any Other Answer: Any Other Answer" — so the
     * label drops it and the chip keeps it: `Answer: Any Answer`, `Other: 1 or 2 or 3`.
     *
     * Which of the two depends on whether anything is targeted, for the same reason the chip's
     * own wording does: "Answer" is only the whole story while nothing else can fire.
     */
    : category === 'any_answer' ? t(hasTargeted ? 'blocks.testBar.categoryOtherAnswers' : 'blocks.testBar.categoryAnswer')
    : category === 'any_response' ? t(hasTargeted ? 'blocks.testBar.categoryOtherResponses' : 'blocks.testBar.categoryResponse')
    : category === 'targeted' ? t('blocks.testBar.categoryTargeted')
    : t('blocks.testBar.categoryIncorrect');
}

/**
 * EVERY EARLIER ANSWER THIS BLOCK LEANS ON, in one row, each showing what it stands for.
 *
 * It began as the row for the recalls with nothing to click — an answer used only inside an
 * equation renders no chip, so the gesture for setting it was unreachable. The owner asked
 * for the general case: "what if we had a row for all the recall chips that are used in the
 * block ... in one row, you see all the recalls, click through them and the setup adjusts to
 * match the recalls you've set ... you always see what state the recalls are in by their
 * symbols and you can manipulate them all in one row."
 *
 * ## The DRIVER is one of them, and it is first
 *
 * A driven block's question is chosen by an earlier answer, which is the same fact as a
 * recall — "what did they answer" — with a bigger consequence. So it shares the row, the
 * gesture and the storage, pinned first and glyphed, rather than being a second mechanism
 * beside them. It is also the only entry a block can have with NO chip anywhere, which is
 * exactly the dependency nothing on screen used to admit to.
 *
 * ## Each entry shows the ANSWER, not the name
 *
 * Owner: "if driven by mcq1, it wouldn't say q1 • mcq1 ... it would say 'penguin' and
 * clicking would switch to bear and flamingo afterwards." The name is on the chip in the
 * prose and in the References panel; what this row is for is the STATE, and the state is the
 * answer. Where a source offers no enumerable answer the entry is not offered at all —
 * `useRecalledHere` drops it rather than showing a control that cannot move.
 *
 * ## And no row where there is nothing to set
 *
 * Owner: "recall row only if recall chips are in use, otherwise no row." Most blocks have
 * none; across the dev database the ones that do carry one to three.
 *
 * `data-answer-ref-pick` is the whole of the wiring. The card's own capture handler cycles
 * anything carrying it, so this row inherits the identical gesture rather than repeating it —
 * the same attribute a prose chip carries.
 */
function RecallSetRow() {
  const { t } = useTranslation();
  const identity = useBlockIdentity();
  /*
   * THE QUOTED VALUES ONLY. The driven entry is a VERSION, not a value in a sentence, and it
   * now sits in the version slot beside Edit — "if it's driven-by, that's actually a version
   * ... it should be visually distinct and not look like the other text cyclers", and "it
   * and the edit button should be together". See `DrivenVersionControl`.
   */
  const rows = useRecalledHere(identity?.blockId).filter(r => !r.driven);
  if (rows.length === 0) return null;
  return (
    /* CENTRED on the bar, where it reads as a statement about the block rather than a control
       hanging off its left edge. */
    <div className='flex min-w-0 flex-wrap items-center justify-center gap-2'>
      {/* A lead-in, because this row sits on a bar with no sentence around it: the dotted
          underline says "a recalled answer" to someone who already knows the vocabulary, and
          names it for everyone else. "Set" was its old word and read as an instruction when
          the entries were names; they are answers now. */}
      <span className='shrink-0 text-xs text-ws-muted'>{t('blocks.answerRef.setRowLabel')}</span>
      {rows.map(row => {
        const mark = row.shown?.tier ? TIER_MARK[row.shown.tier] : undefined;
        return (
          <button
            key={row.aliasId}
            type='button'
            data-answer-ref-pick={row.aliasId}
            /*
             * THE PROSE'S OWN LOOK, not a chip. Owner: "i don't like the chips ... i prefer
             * the underlined text that we have inline in the prose". A chip says "this is a
             * reference"; what the row is for is the ANSWER, and the answer in a sentence is
             * underlined text with a mark beside it.
             */
            className='ws-answer-ref-sample inline-flex items-center gap-0.5 text-sm'
            aria-label={t(row.driven
              ? 'blocks.answerRef.setRowCycleDriven'
              : 'blocks.answerRef.setRowCycle',
            { name: row.name, answer: row.shown?.text ?? '' })}
          >
            {/* The link glyph, the canvas pill's own, on the entry whose consequence is the
                whole question rather than a value inside it. */}
            {row.driven && <Link2 aria-hidden='true' className='mr-0.5 h-3 w-3 shrink-0' />}
            <span aria-hidden='true'>{row.shown?.text || row.name}</span>
            {/* WHICH answer it is, in the vocabulary the sentence already uses — see
                `TIER_MARK`. `aria-hidden` because the label above already names the tier. */}
            {row.shown && (
              <span
                aria-hidden='true'
                data-verdict={row.shown.verdict}
                {...(row.shown.tier ? { 'data-tier': row.shown.tier } : {})}
                className={mark?.ringed
                  ? 'ws-answer-ref-verdict ws-answer-ref-verdict-ringed'
                  : 'ws-answer-ref-verdict'}
              >
                {mark?.glyph ?? VERDICT_GLYPH[row.shown.verdict] ?? VERDICT_GLYPH.neutral}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * ONE CHIP OF A TEST-BAR LEGEND, wherever that legend is drawn.
 *
 * Exported because Behind the Scenes draws the same legend and started by hand-rolling it, which
 * lost three things at once: the colour came from a highlight rather than from the state's own
 * evaluation, a slashed or unreachable chip rendered as neither, and a connector rendered as a
 * chip. ACTIVE IS OPACITY here, never colour — a chip's colour is what kind of state it is, and
 * overriding it to say "this one fired" means a teacher reads the wrong verdict off the tone.
 */
export function LegendChip({ chip: c, compact }: {
  chip: TestBarChip;
  /** Tighter padding for a dense list -- Behind the Scenes draws one row per block. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  if (c.kind === 'connector') {
    return <span className='shrink-0 text-xs italic text-ws-muted'>{c.condition}</span>;
  }
  // The shared "Unreachable" label, NOT the chip's own condition text. A state that can never
  // fire has no condition worth reading, and printing one is actively misleading: Compass with a
  // full-circle tolerance said "Anything but 0° to 360°", describing the empty set as a trigger.
  if (c.reachable === false) {
    return <UnreachableChip width='full' label={c.unreachableLabel ?? t('blocks.feedbackPanel.unreachable')} />;
  }
  return (
    <span className={`${chipClass(c.evaluation, { width: 'full', shrink0: false, italic: c.muted, dimmed: c.muted, compact })} transition-opacity ${c.active ? '' : 'opacity-40'}`}>
      {testBarEvalIcon(c.evaluation)}
      <ChipDivider />
      <SlashedLabel slashed={c.slashed} className='min-w-0 break-words'>{c.condition}</SlashedLabel>
    </span>
  );
}

export function TestBar({ continuous, onContinuousChange, onSubmit, chips, gradingMode, onReset, onReroll, notices, topNotices, compact, constrainedHeight, renderSection = 'all' }: TestBarProps) {
  const { t } = useTranslation();
  const showControls = renderSection !== 'legend';
  const showLegend = renderSection !== 'controls';

  // Shared button pieces — composed differently by the two branches below (grid cells for the
  // default single-bar layout; a plain left-to-right row for the landscape controls bar) rather
  // than duplicated, so the two can't drift out of sync.
  const continuousSwitch = (
    <Switch
      on={continuous}
      // The card underneath opens the editor on click, so the press must stop here.
      onClick={(e) => e.stopPropagation()}
      onChange={onContinuousChange}
      label={t('blocks.testBar.continuous')}
    />
  );
  const continuousLabel = <span className='text-xs text-ws-muted'>{t('blocks.testBar.continuous')}</span>;
  const submitButton = !continuous && (
    <button
      type='button'
      onClick={(e) => { e.stopPropagation(); onSubmit(); }}
      className='rounded-md bg-ws-primary px-4 py-1.5 text-xs font-medium text-ws-btn-on-primary transition-opacity hover:opacity-90'
    >
      {t('blocks.testBar.submit')}
    </button>
  );
  const rerollButton = onReroll && (
    <IconButton
      type='button'
      size='sm'
      onClick={(e) => { e.stopPropagation(); onReroll(); }}
      aria-label={t('blocks.testBar.reroll')}
    >
      <Dices className='h-4 w-4' />
    </IconButton>
  );
  const resetButton = (
    <IconButton
      type='button'
      size='sm'
      onClick={(e) => { e.stopPropagation(); onReset(); }}
      aria-label={t('blocks.testBar.reset')}
    >
      <RotateCcw className='h-4 w-4' />
    </IconButton>
  );

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      // rounded-b-xl + overflow-hidden here (not on the outer card) — this is always the last
      // element in the card while isSelected, so it owns matching the card's own rounded-xl
      // bottom corners; the content area above still needs to overflow freely for its own
      // popovers (e.g. the option listbox), so the clip can't live on a shared ancestor.
      //
      // constrainedHeight adds min-h-0 flex-1, letting this bar be squeezed to a bounded height
      // by a height-constrained wrapper (the phone dialog's capped/rail contexts) so the chip
      // legend below can scroll internally instead of pushing sibling content around — see the
      // prop's own doc comment on TestBarProps for why this must stay opt-in rather than
      // unconditional (desktop's own ancestor chain is not actually a no-op once shrinking is
      // allowed, confirmed live).
      className={`flex ${constrainedHeight ? 'min-h-0 flex-1 ' : ''}flex-col gap-2 overflow-hidden rounded-b-xl border-t border-ws-border bg-ws-hover px-3 py-2.5`}
    >
      {/* Controls — skipped entirely when renderSection is 'legend' (that group wants width the
          phone landscape Test rail can't give it — see the prop's own doc comment). Two
          different compositions of the same button pieces:
          - renderSection 'controls' (the landscape controls bar): a single row, buttons on the
            left and topNotices (branch-resolution/equation text) filling the right — one row
            instead of two stacked, to keep this bar as short as possible where vertical space is
            the scarce axis. topNotices only renders here if truthy, i.e. only when there's
            actually something to show (branching resolved a result) — no separate "if branching"
            check needed beyond that.
          - renderSection 'all' (default — desktop cards, portrait phone bar): unchanged from
            before — three grid columns (Continuous switch left, Submit centered, reroll/reset
            right), with topNotices as its own row below. */}
      {showControls && (renderSection === 'controls' ? (
        <>
          <div className='flex items-center gap-3'>
            <div className='flex shrink-0 items-center gap-1.5'>
              {continuousSwitch}
              {continuousLabel}
            </div>
            {submitButton}
            <div className='flex shrink-0 items-center gap-2'>
              {rerollButton}
              {resetButton}
            </div>
            {topNotices && (
              <div className='min-w-0 flex-1 border-l border-ws-border pl-3'>
                {topNotices}
              </div>
            )}
          </div>
          {/* notices (setup warnings/errors) also lives here rather than in the narrow legend
              rail — confirmed live: this is prose-like text a teacher needs to actually read, not
              a quick-glance chip, and it wrapped into 4+ lines crammed alongside the chip legend
              in the ~144px rail ("smashed"). Its own row, only rendered when there's something to
              show, so the common no-issues case stays the single-row height achieved above. */}
          {/* `empty:hidden` because the panel in this slot decides for ITSELF whether it has
              anything to say — it computes the reference issues a block cannot know about. So
              the slot is always filled and the ROW collapses when the panel renders nothing,
              which keeps the no-issues case the single-row height it was. Gating on the slot
              being non-empty instead is what hid every reference warning on a block that had
              no other problem. */}
          {notices && (
            <div className={`flex flex-col gap-2 empty:hidden ${topNotices ? 'border-t border-ws-border pt-2' : ''}`}>
              {notices}
            </div>
          )}
        </>
      ) : (
        <div className='grid grid-cols-[1fr_auto_1fr] items-center gap-2'>
          <div className='flex items-center justify-start gap-1.5'>
            {continuousSwitch}
            {continuousLabel}
          </div>
          <div className='flex justify-center'>
            {submitButton}
          </div>
          <div className='flex items-center justify-end gap-2'>
            {rerollButton}
            {resetButton}
          </div>
        </div>
      ))}
      {showControls && renderSection !== 'controls' && topNotices}
      {/*
        * OUTSIDE both control layouts, so it renders once in either.
        *
        * It went inside the landscape branch first and was invisible on every desktop card —
        * `renderSection` has three values and the two control layouts are separate subtrees,
        * which is exactly the kind of split a test that mounts only the default would miss.
        * Gated on `showControls` because the legend-only rail is a list of what COULD fire,
        * and this is a control.
        */}
      {showControls && <RecallSetRow />}
      {/* Scrollable body (constrainedHeight only) — chip legend + notices, the two sections that
          grow with however many targeted-feedback states/branches/warnings a block has. min-h-0
          is load-bearing: without it a flex-1 item won't shrink below its content's natural
          height even inside a height-constrained ancestor (the same gotcha already called out in
          feedback-panel.tsx). Keeping this as its own scrolling region — rather than letting it
          grow unbounded — is what keeps the control row and topNotices above always reachable
          and stops a block with many chips from squeezing testSlot down to nothing. Plain,
          unwrapped flex-col otherwise (desktop) — same natural content height as before. Skipped
          entirely when renderSection is 'controls'. */}
      {showLegend && (
      <div className={constrainedHeight ? 'flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto' : 'flex flex-col gap-3'}>
        {chips.length === 0 ? (
          // Not a fired/unfired chip like the rest of the legend — Optional mode has no
          // evaluation to track, so this reads the same regardless of what's selected or
          // whether anything's been submitted at all, permanently in its neutral (never dimmed)
          // state rather than toggling active like every other chip here.
          <span className={`${chipClass('neutral', { width: 'auto' })} w-fit`}>
            {testBarEvalIcon('neutral')}
            <ChipDivider />
            {t(gradingMode === 'optional' ? 'blocks.testBar.noFeedback' : 'blocks.testBar.noFeedbackYet')}
          </span>
        ) : (
          groupChipsByTrack(chips).map(({ track, categories, anyTargeted }) => (
            // A label column + a wrapping chip column, one row per category — reads as a small
            // legend/key instead of every label and chip fighting for space in one flowing line.
            // compact stacks label above chips instead (a flex-col of the same Fragment children,
            // in the same order) so the chip row gets the full column width — see the prop's own
            // doc comment on TestBarProps.
            <div key={track ?? '_untracked'} className={compact ? 'flex flex-col gap-1' : 'grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1'}>
              {categories.map(({ category, chips: catChips }) => {
                const label = catChips.find(c => c.categoryLabel)?.categoryLabel
                  ?? testBarCategoryLabel(category, t, anyTargeted);
                return (
                  <React.Fragment key={category}>
                    <span className='shrink-0 text-[10px] font-semibold uppercase tracking-wide text-ws-muted'>
                      {label}
                    </span>
                    <div className='flex flex-wrap items-center gap-1'>
                      {/* max-w-full (not shrink-0) + the condition text's own min-w-0 — a chip
                          whose full text is wider than the row itself (the compact legend rail is
                          only ~135px, and a label like Keyword's "Any Unlisted Word" catch-all
                          comes close to that on its own) needs to wrap its text across two lines
                          instead of forcing the row into horizontal scroll. shrink-0 here would
                          hold the chip at its natural (too-wide) content size regardless; a plain
                          text child of a flex row also won't wrap on its own (flex items default
                          to min-width:auto, i.e. never shrink below their unwrapped text width)
                          unless it's given its own min-w-0. Confirmed live: without both of these,
                          the legend rail grew a real horizontal scrollbar under this exact chip. */}
                      {catChips.map(c => <LegendChip key={c.id} chip={c} />)}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          ))
        )}
        {/* renderSection 'legend' skips notices here — it moved to the controls bar's own row
            (see above) since it's prose-like warning text that read "smashed" crammed alongside
            the chip legend in the narrow rail, confirmed live. Unchanged for 'all'. */}
        {renderSection !== 'legend' && notices && (
          <div className='flex flex-col gap-2 border-t border-ws-border pt-2 empty:hidden'>
            {notices}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// ─── The card's bottom bar ─────────────────────────────────────────────────────
// Every input block ends its unselected card with one horizontal status bar, in one of three
// tones. That bar was hand-built eleven times, and it drifted exactly as you'd expect: slider
// carried a darker `bg-ws-success-bg` and `text-xs`/70%-opacity text against everyone else's
// `bg-ws-success-fg/[7%]` and full-opacity `text-sm`. The chrome (background, icon, padding, text
// treatment, and the trailing-chip layout switch) lives here now; blocks supply only what their
// bar SAYS, which is the part that genuinely differs per block.

export type CollapsedBarTone = 'success' | 'warning' | 'error';

const COLLAPSED_BAR_TONE_CLASS: Record<CollapsedBarTone, string> = {
  // Not `bg-ws-success-bg`: this bar sits under a card's own surface and wants a far lighter wash
  // than the solid success background used for chips and inline callouts.
  success: 'bg-ws-success-fg/[7%] text-ws-success-fg',
  warning: 'bg-ws-warning-bg text-ws-warning-fg',
  error: 'bg-ws-error-bg text-ws-error-fg',
};
const COLLAPSED_BAR_TONE_ICON: Record<CollapsedBarTone, React.ElementType> = {
  success: Check,
  warning: AlertTriangle,
  error: OctagonAlert,
};

export interface CollapsedBlockBarProps {
  tone: CollapsedBarTone;
  /** Overrides the tone's default icon where the bar means something more specific than its tone
   * — e.g. Numerical's missing-feedback warning, which uses MessageSquareOff. */
  icon?: React.ElementType;
  /** Top-aligns the icon (`mt-0.5`) for bars whose content wraps to more than one line, e.g. a
   * formula answer fanning out into several chips. */
  align?: 'center' | 'start';
  /** A chip pinned to the bar's right edge, present in every tone — the "Mobile only" marker on
   * the three sensor blocks. Switches the row to `justify-between` and groups icon+content on the
   * left; without it the row keeps its original single-group layout exactly (an unconditional
   * wrapper would change how long text wraps). */
  trailing?: React.ReactNode;
  /** Lets the bar scroll sideways rather than squeeze, for content with a hard minimum width
   * (GPS's coordinate pair). */
  scrollX?: boolean;
  children: React.ReactNode;
}

export function CollapsedBlockBar({ tone, icon, align = 'center', trailing, scrollX, children }: CollapsedBlockBarProps) {
  const Icon = icon ?? COLLAPSED_BAR_TONE_ICON[tone];
  const iconEl = <Icon aria-hidden='true' className={`h-4 w-4 shrink-0 ${align === 'start' ? 'mt-0.5' : ''}`} />;
  const group = align === 'start' ? 'flex items-start gap-2' : 'flex items-center gap-2';
  // `align` applies to whichever row actually holds the icon and content. With a trailing chip
  // that's the inner group, and the outer row stays centered so the chip sits mid-height; without
  // one the outer row IS that row. Getting this wrong is invisible until the content wraps to a
  // second line, which is exactly when a top-aligned icon matters.
  return (
    <div className={`flex ${trailing ? 'items-center justify-between ' : align === 'start' ? 'items-start ' : 'items-center '}gap-2 px-2 py-2.5 text-sm ${scrollX ? 'overflow-x-auto ' : ''}${COLLAPSED_BAR_TONE_CLASS[tone]}`}>
      {trailing ? <div className={`min-w-0 ${group}`}>{iconEl}{children}</div> : <>{iconEl}{children}</>}
      {trailing}
    </div>
  );
}

/** The healthy card's one-line bar: which grading mode this block is in, in that block's own
 * words. The mode-to-key mapping is fixed (`barSurvey`/`barOptional`/`barCorrect`); only the
 * block prefix varies, because the sentences genuinely do — six blocks share "All Answers (Except
 * Blanks) Accepted" while Compass says "Any Heading Accepted" and GPS "Any Location Accepted".
 * So the STRINGS stay per-block and the branching does not: three blocks had written the same
 * mapping as three separate if-blocks, two as this one-liner.
 *
 * Scale is deliberately not a caller — its graded case is a genuinely different rule (the axis
 * already draws the answer key, so the bar only confirms one is set, and only in phone portrait).
 */
export function collapsedModeBar(
  gradingMode: GradingMode,
  t: (key: string) => string,
  blockPrefix: string,
  trailing?: React.ReactNode,
  /**
   * A narrower sentence for a block whose SETUP intercepts answers before the catch-all sees
   * them — Numerical with scientific notation, significant figures or units required.
   *
   * The fixed string claims too much there: "All Answers (Except Blanks) Accepted" on a block
   * that is rejecting `3` for not being in scientific notation. It was already approximate —
   * Incomplete Answer gates a units question too and the sentence never mentioned it — but one
   * unnamed exception is a rounding and three is a wrong statement.
   */
  surveyKeyOverride?: string,
): React.ReactNode {
  const key = gradingMode === 'survey' ? (surveyKeyOverride ?? 'barSurvey')
    : gradingMode === 'optional' ? 'barOptional' : 'barCorrect';
  return (
    <CollapsedBlockBar tone='success' trailing={trailing}>
      <span>{t(`blocks.${blockPrefix}.${key}`)}</span>
    </CollapsedBlockBar>
  );
}

/** What the collapsed card shows where the question would be, before one is written. Eleven
 * blocks rendered this identical paragraph, against two different strings: nine said "No question
 * yet" and MCQ/Numerical said "No question — tap to edit".
 *
 * "No question yet" won on three counts. "Tap" names one input device on a card that is a click
 * target on desktop and a tap target on phone, so it is wrong half the time. The instruction is
 * redundant anyway — the whole card is a role='button' that opens the editor from anywhere, and
 * the status bar directly below already says "Question: Question text is blank", which is the
 * actionable half; this paragraph's job is only to label the gap. And dropping the clause drops
 * an em-dash the warning-copy rule would rather not have.
 *
 * The content blocks (text/image/sound/dataviz) follow the same "No <thing> yet" shape, but keep
 * their own keys: the noun genuinely differs per block, only the pattern is shared. */


/** The "needs a phone to answer" marker the three sensor blocks pin to the right of their bar, in
 * every state — informational only, no enforcement, so a teacher scanning the canvas can always
 * tell which blocks a laptop can't answer. Was three identical definitions and three identical
 * locale keys; the string is one key now, since three translations of "Mobile only" can disagree
 * and the concept cannot. */
export function MobileOnlyChip() {
  const { t } = useTranslation();
  return (
    <span className={chipClass('neutral', { width: 'auto' })}>
      <Smartphone aria-hidden='true' className='h-3 w-3 shrink-0' />{t('blocks.sensorChip.mobileOnly')}
    </span>
  );
}

/** The shared "highest-severity issue wins the card's bar" rule — errors before warnings, one
 * line, bold category then detail. Returns null when there's nothing wrong, which is the signal
 * for a block's own bar builder to fall through to whatever it says when healthy.
 *
 * This exact reduction was written out nine times. It is the card-side twin of IssuesPanel (which
 * lists all of them in the editor), and the two disagreeing is precisely the failure this
 * prevents: slider's card once painted amber for a defect the editor painted red. */
export function collapsedIssueBar(issues: BlockIssue[], trailing?: React.ReactNode): React.ReactNode | null {
  // Filtered BEFORE ranking, not after: a skipped error sitting at the top would otherwise hide a
  // real error underneath it, which is the opposite of the point. When everything is skipped the
  // bar falls through to whatever the block says when healthy — a card with no question but a
  // configured answer shows the answer, with the gap named in the card body directly above.
  const voiced = issues.filter(i => !i.shownInPlace);
  const top = voiced.find(i => i.severity === 'error') ?? voiced.find(i => i.severity === 'warning');
  if (!top) return null;
  return (
    <CollapsedBlockBar tone={top.severity === 'error' ? 'error' : 'warning'} trailing={trailing}>
      <span><strong className='font-semibold'>{top.category}:</strong> {top.detail}</span>
    </CollapsedBlockBar>
  );
}

export interface CollapsedInputBlockProps {
  block: Block;
  /** Ref returned by useArmedDelete — attached to the outermost container */
  blockRef: React.RefObject<HTMLDivElement>;
  /** Optional ref for the clickable content area (for focus management on edit→collapse transition) */
  contentRef?: React.Ref<HTMLDivElement>;
  /** The block-type icon, e.g. <SquareCheck /> or <Hash /> */
  icon: React.ReactNode;
  /** Block type name shown in the full-layout header (phone portrait) */
  titleLabel: string;
  isMain: boolean;

  // Grading mode
  gradingMode: GradingMode;
  gradingModeLabel: string;
  GradingModeIcon: React.ElementType;
  /** Tailwind classes for the mode chip in the full-view header, e.g. 'bg-ws-primary/10 text-ws-primary' */
  gradingModeChipClass: string;

  // Selection / drag state
  isSelected?: boolean;
  isDragOver?: boolean;

  // Armed-delete state (from useArmedDelete in block-actions)
  isDeleteArmed: boolean;
  onDisarmDelete: () => void;
  onTriggerDuplicate: () => void;
  onTriggerDelete: () => void;

  // Content interaction
  /** Accessible label for the clickable content area */
  a11yLabel: string;
  /** Called when the user activates the block (click or Enter/Space) to enter editing */
  onActivate: () => void;

  /** Block-specific display content */
  children: React.ReactNode;

  /** Optional answer-status bar rendered at the bottom of the card — hidden while isSelected
   * (the test bar, if provided, takes this slot instead).
   *
   * Took a `previewValues` argument until now. That was for a card-level variable preview with its
   * own reroll, since removed: both layouts had been passing a hard-coded `null` ever since, and
   * only Numerical still read it, behind a branch that could no longer be reached. */
  answerBarSlot?: () => React.ReactNode;

  /**
   * While `isSelected` (this block's own editor is open elsewhere), colors the border to
   * reflect a live test result instead of the default selection highlight, and hides the
   * header/duplicate-delete chrome + bottom bar so the card reads as a clean student-facing
   * surface with only its actual interactive content inside a border. `undefined` (no test
   * answer checked yet) keeps the default selection-highlight border.
   */
  testEvaluation?: 'correct' | 'incorrect' | 'neutral';

  /** Renders the teacher-facing test bar below the content area while isSelected — omit for
   * blocks that haven't been retrofitted with live-test mode yet (they just get the plain
   * selection-highlighted card, same as before this existed). */
  testBar?: Omit<TestBarProps, 'gradingMode'>;
}

export function CollapsedInputBlock({
  block, blockRef, contentRef, icon, titleLabel, isMain,
  gradingMode, gradingModeLabel, GradingModeIcon, gradingModeChipClass,
  isSelected, isDragOver,
  isDeleteArmed, onDisarmDelete, onTriggerDuplicate, onTriggerDelete,
  a11yLabel, onActivate,
  children,
  answerBarSlot,
  testEvaluation,
  testBar,
}: CollapsedInputBlockProps) {

  const testBorderClass =
    testEvaluation === 'correct'   ? 'border-2 border-ws-success-fg' :
    testEvaluation === 'incorrect' ? 'border-2 border-ws-error-fg' :
    testEvaluation === 'neutral'   ? 'border-2 border-ws-neutral-fg' :
    null;

  // While isSelected, this card is the live test surface — its own interactive children
  // (a real <select>, a real button, etc.) handle their own clicks/focus/keyboard, so the
  // wrapping content area must NOT also behave like a single activation button (that would
  // both re-trigger onActivate needlessly and nest a role='button' around real controls,
  // which screen readers can't sensibly announce).
  const contentProps = isSelected ? {} : {
    tabIndex: 0 as const,
    role: 'button' as const,
    'aria-label': a11yLabel,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (isDeleteArmed) { onDisarmDelete(); return; }
        onActivate();
      }
    },
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      if (e.detail === 0) return; // keyboard-fired click, already handled by onKeyDown
      if (isDeleteArmed) { onDisarmDelete(); return; }
      onActivate();
    },
  };


  if (isMain) {
    const barNode = answerBarSlot ? answerBarSlot() : null;
    return (
      <BlockCard
        block={block}
        cardRef={blockRef}
        icon={icon}
        gradingMode={(
          <GradingModeIcon
            aria-hidden='true'
            className={`h-3.5 w-3.5 ${gradingMode === 'graded' ? 'text-ws-primary' : 'text-ws-muted'}`}
          />
        )}
        rails={!isSelected}
        isDeleteArmed={isDeleteArmed}
        isSelected={isSelected}
        isDragOver={isDragOver}
        testBorder={testBorderClass}
        onDuplicate={onTriggerDuplicate}
        onDelete={onTriggerDelete}
        onDisarm={onDisarmDelete}
      >

        {/* Middle column — content area + optional answer/test bar */}
        <div className='flex min-w-0 flex-1 flex-col'>
          <div
            ref={contentRef}
            {...contentProps}
            className={`relative flex-1 p-4 group/content focus-visible:[outline:2px_auto] transition-colors ${isSelected ? 'rounded-xl' : ''} ${isSelected ? '' : 'cursor-pointer'}`}
          >
            {children}
          </div>
          {isSelected && testBar ? (
            // shrink-0 keeps this from being squeezed by the block-list's own ancestor flex-1
            // chain the moment constrainedHeight makes it shrinkable (confirmed live: without
            // shrink-0 here, a handful of chips was enough for that chain to compress this whole
            // card down to an "equal share" of the page instead of its own natural size) — so
            // under the generous max-h-[420px] cap, a normal chip count still just grows the card
            // and lets the page scroll, exactly as before constrainedHeight existed. Only a
            // genuinely excessive chip count (past ~420px) caps here and scrolls the legend
            // internally instead of ballooning the card further.
            <div className='flex max-h-[420px] shrink-0 flex-col'>
              <TestBar {...testBar} gradingMode={gradingMode} constrainedHeight />
            </div>
          ) : !isSelected && barNode && (
            <div
              className='border-t border-ws-border'
              onClick={(e) => { e.stopPropagation(); if (!isDeleteArmed) onActivate(); }}
            >
              {barNode}
            </div>
          )}
        </div>

      </BlockCard>
    );
  }

  // Alternative (non-main) layout
  const barNode = answerBarSlot ? answerBarSlot() : null;
  return (
    <BlockPanelCard
      blockId={block.id}
      cardRef={blockRef}
      icon={icon}
      title={titleLabel}
      header={!isSelected}
      isDeleteArmed={isDeleteArmed}
      isSelected={isSelected}
      isDragOver={isDragOver}
      testBorder={testBorderClass}
      onDuplicate={onTriggerDuplicate}
      onDelete={onTriggerDelete}
      onDisarm={onDisarmDelete}
    >

      {/* Content area */}
      <div
        ref={contentRef}
        {...contentProps}
        className={`relative p-4 group/content focus-visible:[outline:2px_auto] transition-colors ${isSelected ? 'rounded-xl' : ''} ${isSelected ? '' : 'cursor-pointer'}`}
      >
        {children}
      </div>

      {/* Answer/test bar — hidden while not selected shows grading chip + block-specific
          content; while live-testing shows the test bar instead (border color already carries
          correctness, so the grading chip is dropped here). */}
      {isSelected && testBar ? (
        // Same shrink-0 + generous max-height cap as the isMain layout's own TestBar — see its
        // comment for why constrainedHeight needs this wrapper rather than being left to squeeze
        // freely in whatever ancestor flex chain this card happens to sit in.
        <div className='flex max-h-[420px] shrink-0 flex-col'>
          <TestBar {...testBar} gradingMode={gradingMode} constrainedHeight />
        </div>
      ) : !isSelected && (
        <div
          className='rounded-b-xl overflow-hidden border-t border-ws-border'
          onClick={(e) => { e.stopPropagation(); if (!isDeleteArmed) onActivate(); }}
        >
          <div className='flex items-stretch'>
            <span className={`flex shrink-0 items-center border-r border-ws-border px-3 py-2.5 ${gradingModeChipClass}`} aria-label={gradingModeLabel}>
              <GradingModeIcon aria-hidden='true' className='h-3.5 w-3.5' />
            </span>
            {barNode && <div className='flex-1 min-w-0'>{barNode}</div>}
          </div>
        </div>
      )}
    </BlockPanelCard>
  );
}

// Moved to variable-chip-preview, beside the BlockQuestion that owns when it shows.
export { EmptyQuestionNote } from './variable-chip-preview';
